import { TabList, TabPanel, useTabStore } from '@ariakit/react/tab';
import { Children, useMemo, useRef } from 'react';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { ActiveTabIndicator } from './active-tab-indicator/ActiveTabIndicator.tsx';
import { AnimatedTabsProvider } from './context/AnimatedTabsContext.tsx';
import { TabPanels } from './tab-panels/TabPanels.tsx';
import TabsStyles from './TabLinks.module.css';

type Props = {
  /** `Tab` components, wrapped in a fragment*/
  tabs: React.ReactElement;
  /** `Route` components, matching the paths in the `Tab` components */
  children: React.ReactNode;
  /** Query param key to match on. Defaults to `tab`. */
  queryParamKey?: string;
};

/**
 * Creates a tabbed interface with an animated underline to indicate the active tab.
 */
export function TabLinks({ tabs, children, queryParamKey = 'tab' }: Props) {
  const { state } = useAppLocation();
  const ref = useRef<HTMLDivElement>(null);
  const paramSchema = useMemo(() => ({ [queryParamKey]: 'scalar' as const }), [queryParamKey]);
  const [params, setParams] = useUrlParams(paramSchema);
  const tab = useTabStore({
    selectedId: params[queryParamKey],
    setSelectedId: (id) => {
      // setSelectedId may be called more than once for the same id, so we make
      // sure we only navigate once.
      if (id && id !== params[queryParamKey]) {
        setParams(
          { [queryParamKey]: id },
          {
            // Propagate the current state so `from` still works
            state,
            flushSync: true,
          }
        );
      }
    },
  });

  const tabPanels = Children.map(children, (child) => <TabPanel store={tab}>{child}</TabPanel>);

  return (
    <AnimatedTabsProvider>
      <TabList store={tab} ref={ref} className={TabsStyles.TabLinks}>
        <ActiveTabIndicator tabsRef={ref} />
        {tabs}
      </TabList>
      <TabPanels>{tabPanels}</TabPanels>
    </AnimatedTabsProvider>
  );
}
